import * as React from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { Box, Button, styled, TextField } from "@mui/material";
import { dividerClasses } from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import {
  Ingredient,
  ModalNames,
  ShoppingListItem,
  State,
  StateProps,
} from "./state";
import { useEffect, useState } from "react";
import HistoryIcon from "@mui/icons-material/History";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import { saveState } from "../api";
import { localStorageEmailKey } from "../App";
import { toggleModal } from "./modals";

export interface ShoppingListOptions extends StateProps {
  readonly setCopySnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  readonly setCopySnackbarText: React.Dispatch<React.SetStateAction<string>>;
}

const BootstrapButton = styled(Button)({
  boxShadow: "none",
  textTransform: "none",
  fontSize: 16,
  padding: "6px 12px",
  border: "1px solid",
  borderRadius: "50%",
  width: "0.25em",
  height: "1.5em",
  minWidth: "0px",
  lineHeight: 1.5,
  borderColor: "#0063cc",
  "&:active": {
    backgroundColor: "red",
    borderColor: "#0062cc",
  },
  "&:focus": {
    boxShadow: "0 0 0 0.2rem rgba(0,123,255,.5)",
  },
});

export interface ClickedShoppingListItemProps {
  setState: React.Dispatch<React.SetStateAction<State>>;
  ingredientName: string;
}
function clickedShoppingListItem(props: ClickedShoppingListItemProps) {
  const { setState, ingredientName } = props;
  setState((prevState: State) => {
    return {
      ...prevState,
      itemsInCart: [...(prevState.itemsInCart ?? []), ingredientName],
    };
  });
}

function undoShoppingListItem(props: StateProps) {
  const { setState } = props;
  setState((prevState: State) => {
    const undoItemsInCart = prevState.itemsInCart ?? [];
    return {
      ...prevState,
      itemsInCart: undoItemsInCart.slice(0, -1),
    };
  });
}

function copyToClipboard(
  shoppingList: Record<string, ShoppingListItem>,
  setCopySnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setCopySnackbarText: React.Dispatch<React.SetStateAction<string>>,
) {
  const ingredientsArray: string[] = [];
  Object.values(shoppingList).forEach((item) => {
    if (item.visible) {
      ingredientsArray.push(`${item.amount} ${item.name}`);
    }
  });
  const text = ingredientsArray.join("\n");
  navigator.clipboard
    .writeText(text)
    .then(() => {
      setCopySnackbarText("Copied!");
      setCopySnackbarOpen(true);
    })
    .catch((error) => {
      setCopySnackbarText("Failed to copy");
      setCopySnackbarOpen(true);
    });
}

function clearWeek(props: StateProps) {
  const { setState } = props;
  toggleModal({ setState, name: ModalNames.CONFIRMATION });
}

function sortShoppingListItems(items: ShoppingListItem[], state: State) {
  const ingredientToIsle = Object.entries(
    state.settings?.isleToIngredients ?? {},
  ).reduce<Record<string, number>>((acc, [key, arr]) => {
    for (const val of arr) {
      acc[val] = Number(key);
    }
    return acc;
  }, {});
  const sorted = [...items].sort((a, b) => {
    const orderA = ingredientToIsle[a.name] ?? Infinity;
    const orderB = ingredientToIsle[b.name] ?? Infinity;
    if (orderA === orderB) {
      return a.name.localeCompare(b.name);
    }

    return orderA - orderB;
  });

  return sorted;
}

function getShoppingListElements(
  rawItems: Record<string, ShoppingListItem>,
  state: State,
  setState: React.Dispatch<React.SetStateAction<State>>,
) {
  const sortedItems = sortShoppingListItems(Object.values(rawItems), state);
  // TODO - next step - get previous week
  return Object.values(sortedItems).map((item) => {
    return (
      <>
        <ListItem
          key={`${item.name}-list-item`}
          alignItems="center"
          sx={{ display: item.visible ? "flex" : "none" }}
        >
          <BootstrapButton
            key={`${item.name}-bootstrap-button`}
            onClick={() =>
              clickedShoppingListItem({
                ingredientName: item.name,
                setState,
              })
            }
            variant="outlined"
          ></BootstrapButton>
          <ListItemText
            key={`${item.name}-list-item-text`}
            sx={{ textAlign: "center" }}
            primary={`${item.amount} ${item.name}`}
            onClick={() =>
              clickedShoppingListItem({
                ingredientName: item.name,
                setState,
              })
            }
          />
        </ListItem>
      </>
    );
  });
}

export interface AddToNewShoppingList {
  readonly name: string;
  readonly newShoppingList: Record<string, ShoppingListItem>;
  readonly ingredient: Ingredient;
  readonly state: State;
}
function addToNewShoppingList(props: AddToNewShoppingList) {
  const { newShoppingList, name, ingredient, state } = props;
  if (Object.keys(newShoppingList).includes(name)) {
    newShoppingList[name].amount += 1;
    newShoppingList[name].visible =
      // binary expression is never nullish
      !(state.itemsInCart?.includes(ingredient.name) ?? false);
  } else {
    newShoppingList[name] = {
      amount: 1,
      name: ingredient.name,
      visible: !(
        // binary expression is never nullish
        (state.itemsInCart?.includes(ingredient.name) ?? false)
      ),
    };
  }
}

export default function ShoppingList(options: ShoppingListOptions) {
  const { state, setState } = options;
  const [shoppingList, setShoppingList] = useState<
    Record<string, ShoppingListItem>
  >({});
  const [additionalItem, setAdditionalItem] = useState("");

  const addAdditionalItem = () => {
    setState((prevState: State) => {
      return {
        ...prevState,
        additionalItems: [
          ...prevState.additionalItems,
          additionalItem.toLowerCase().trim(),
        ],
      };
    });
    setAdditionalItem("");
  };

  useEffect(() => {
    const newShoppingList: Record<string, ShoppingListItem> = {};
    Object.values(state.menu).forEach((menuItem) => {
      menuItem.recipe?.instructions.forEach((instruction) => {
        instruction.ingredients?.forEach((ingredient) => {
          if (ingredient.showAsShoppingListItem) {
            const name = ingredient.name;
            addToNewShoppingList({
              ingredient,
              name,
              newShoppingList,
              state,
            });
          }
        });
      });
    });
    state.additionalItems.forEach((item) => {
      addToNewShoppingList({
        ingredient: {
          name: item,
          showAsShoppingListItem: true,
        },
        name: item,
        newShoppingList,
        state,
      });
    });
    setShoppingList(newShoppingList);
  }, [state]);

  return (
    <>
      <Accordion
        defaultExpanded={options.state.settings?.shoppingListExpanded ?? false}
      >
        <AccordionSummary
          expandIcon={<ArrowDropDownIcon />}
          aria-controls="mealSelection-content"
          id="mealSelection-header"
        >
          <Typography component="span">Shopping List</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              paddingBottom: "1em",
            }}
          >
            <TextField
              label="Additional Item"
              variant="outlined"
              size="small"
              sx={{ flexGrow: 1 }}
              value={additionalItem}
              onChange={(e) => setAdditionalItem(e.target.value)}
            />
            <Button variant="contained" onClick={addAdditionalItem}>
              Add
            </Button>

            {/* <ContentPasteGoIcon
              sx={{ cursor: "pointer" }}
              onClick={() =>
                copyToClipboard(
                  shoppingList,
                  options.setCopySnackbarOpen,
                  options.setCopySnackbarText,
                )
              }
              fontSize="large"
            />
            <RemoveShoppingCartIcon
              sx={{ cursor: "pointer" }}
              onClick={() => clearWeek({ ...options })}
              fontSize="large"
            />
            <HistoryIcon
              sx={{ cursor: "pointer" }}
              fontSize="large"
              onClick={() => undoShoppingListItem({ ...options })}
            /> */}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
              margin: "auto",
              justifyContent: "center",
              color: "text.secondary",
              "& svg": {
                m: 1,
              },
              [`& .${dividerClasses.root}`]: {
                mx: 0.5,
              },
            }}
          >
            <List
              sx={{ width: "100%", maxWidth: 360, bgcolor: "background.paper" }}
            >
              {getShoppingListElements(shoppingList, state, setState)}
            </List>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-evenly",
              alignItems: "center",
              width: "100%",
              paddingTop: "2em",
            }}
          >
            <ContentPasteGoIcon
              sx={{ cursor: "pointer" }}
              onClick={() =>
                copyToClipboard(
                  shoppingList,
                  options.setCopySnackbarOpen,
                  options.setCopySnackbarText,
                )
              }
              fontSize="large"
            />
            <RemoveShoppingCartIcon
              sx={{ cursor: "pointer" }}
              onClick={() => clearWeek({ ...options })}
              fontSize="large"
            />
            <HistoryIcon
              sx={{ cursor: "pointer" }}
              fontSize="large"
              onClick={() => undoShoppingListItem({ ...options })}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </>
  );
}
